// Client-side vector outline rendering for England regulatory layers (BBB-457).
//
// WHY THIS EXISTS: the England designation layers are served as WMS tiles behind
// GeoWebCache, which serves pre-rendered tiles and ignores SLD_BODY — so the
// tiles cannot be recoloured upstream at all (verified 07/08/2026: an aligned
// 256x256 request returns GeoServer's default magenta; the same bbox at 257x257
// misses the cache and honours our style). Recolouring therefore has to happen
// here, from the raw features.
//
// Boundaries are drawn as Polylines rather than a google.maps.Data layer because
// Data's StyleOptions has no dash support, so dashed/dotted differentiation — the
// "don't rely on colour alone" requirement — is only reachable via Polyline
// `icons`. Fills are drawn as companion Polygons (see polygonOptionsFor); an
// earlier revision of this comment said the treatment was outline-only with no
// fills, which stopped being true once the faint fill was added.

import { postArcgisQuery } from '../arcgisQuery';
import { reportError } from '../report';
import { DEFAULT_FILL_OPACITY } from '../fillOpacity';

// Web Mercator tile size, used to convert a zoom level into degrees per pixel.
const TILE_SIZE = 256;

// Simplify to roughly this many screen pixels of positional error. Invisible at
// the rendered zoom, but the payload saving is enormous: an unsimplified SPA
// viewport is ~43 MB, versus ~477 KB simplified at zoom 8.
const SIMPLIFY_PIXELS = 1.5;

// Runaway guard. With the minZoom gate a viewport should need one page; this
// only bounds the pathological case rather than looping until the tab dies.
const MAX_PAGES = 5;

const DEFAULT_PAGE_SIZE = 1000;

/**
 * Degrees of positional tolerance to allow when simplifying, for a zoom level.
 *
 * ArcGIS interprets maxAllowableOffset in the units of `inSR` — degrees here —
 * so this converts "about N screen pixels" into degrees at the given zoom.
 * Scaling with zoom is what keeps the payload flat: without it, zooming out
 * multiplies both feature count and per-feature vertex count at once.
 *
 * @param {number} zoom Google Maps zoom level
 * @param {number} [pixels] tolerable error in screen pixels
 * @returns {number} offset in degrees
 */
export function offsetForZoom(zoom, pixels = SIMPLIFY_PIXELS) {
  const degreesPerPixel = 360 / (TILE_SIZE * 2 ** zoom);
  return degreesPerPixel * pixels;
}

/**
 * Build the query parameters for one page of a viewport request.
 *
 * @param {object} args
 * @param {object} args.source catalogue source (needs serviceUrl, nameField)
 * @param {{west: number, south: number, east: number, north: number}} args.bounds
 * @param {number} args.zoom
 * @param {number} [args.resultOffset] page offset for pagination
 * @returns {URLSearchParams}
 */
export function buildViewportParams({ source, bounds, zoom, resultOffset = 0 }) {
  const pageSize = source.pageSize ?? DEFAULT_PAGE_SIZE;
  const params = new URLSearchParams({
    f: 'geojson',
    where: '1=1',
    geometry: `${bounds.west},${bounds.south},${bounds.east},${bounds.north}`,
    geometryType: 'esriGeometryEnvelope',
    inSR: '4326',
    outSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
    // Only the label field — attribute bloat is pure waste when the sole use is
    // the click-to-identify popup.
    outFields: source.nameField ?? 'NAME',
    returnGeometry: 'true',
    maxAllowableOffset: String(offsetForZoom(zoom, source.simplifyPixels)),
    resultRecordCount: String(pageSize),
  });

  if (resultOffset > 0) {
    params.set('resultOffset', String(resultOffset));
  }

  return params;
}

/**
 * Fetch every feature intersecting the viewport, following pagination.
 *
 * @returns {Promise<{features: Array, truncated: boolean}>} truncated is true if
 *   the server still had more to give when we stopped asking.
 */
export async function fetchViewportFeatures(source, bounds, zoom) {
  const collected = [];
  let page = 0;
  let more = true;
  // Summed across pages, for the metrics harness. Costs nothing to carry.
  let ms = 0;
  let bytes = 0;

  while (more && page < MAX_PAGES) {
    const params = buildViewportParams({
      source,
      bounds,
      zoom,
      // Advance by what actually came back, never by the requested page size:
      // resultRecordCount is a request, and ArcGIS clamps it to the layer's own
      // maxRecordCount. Every paged source currently asks for exactly its
      // service's cap (measured against ?f=json on 12/08/2026), so no page is
      // short today and nothing is being dropped. What this removes is the
      // dependency on that staying true — a cap lowered server-side, or a page
      // shortened by a response-size limit or maxRecordCountFactor, would
      // otherwise start the next page past wherever the server actually stopped
      // and drop the gap with no error anywhere to show for it.
      resultOffset: collected.length,
    });
    // eslint-disable-next-line no-await-in-loop -- pages are inherently sequential
    const { features, truncated, ms: pageMs, bytes: pageBytes } = await postArcgisQuery(
      source.serviceUrl,
      params,
      source.id,
    );
    collected.push(...features);
    more = truncated;
    page += 1;
    ms += pageMs || 0;
    bytes += pageBytes || 0;

    // An offset that follows the collected count cannot move past an empty
    // page, so a server reporting more while returning nothing would have us
    // refetch the same page until MAX_PAGES. Stop, leaving `more` true so the
    // result is still reported as incomplete.
    if (more && features.length === 0) break;
  }

  return { features: collected, truncated: more, ms, bytes, pages: page };
}

/** Monotonic clock where available; Date.now only as a fallback for old jsdom. */
function nowMs() {
  return typeof performance !== 'undefined' && performance.now
    ? performance.now()
    : Date.now();
}

/** GeoJSON is [lng, lat]; Google Maps wants {lat, lng}. */
function toLatLngPath(ring) {
  return ring
    .filter((c) => Array.isArray(c) && c.length >= 2)
    .map(([lng, lat]) => ({ lat, lng }));
}

/**
 * Walk the polygons of each feature, yielding one entry per polygon part with
 * its rings intact (outer first, holes after).
 *
 * A single designation is frequently many disjoint parcels, so MultiPolygon
 * parts are flattened into separate entries — but the ring structure WITHIN a
 * part is preserved, because a hole has to stay associated with its outer ring.
 */
function polygonPartsFromFeatures(features, nameField) {
  const parts = [];

  for (const feature of features || []) {
    const geometry = feature?.geometry;
    if (!geometry) continue;

    const name = feature.properties?.[nameField] ?? null;

    // Polygon => [ring][point][x,y]; MultiPolygon => [polygon][ring][point][x,y]
    const polygons =
      geometry.type === 'MultiPolygon'
        ? geometry.coordinates
        : geometry.type === 'Polygon'
          ? [geometry.coordinates]
          : [];

    for (const polygon of polygons) {
      const rings = (polygon || [])
        .filter((ring) => Array.isArray(ring) && ring.length >= 2)
        .map(toLatLngPath)
        .filter((path) => path.length >= 2);

      if (rings.length) parts.push({ name, rings });
    }
  }

  return parts;
}

/**
 * Every ring as its own path, for drawing boundaries.
 *
 * Holes get their own outline here deliberately — the edge of a hole is a real
 * boundary the farmer is standing next to.
 *
 * @returns {Array<{name: string|null, path: Array<{lat: number, lng: number}>}>}
 */
export function ringsFromFeatures(features, nameField = 'NAME') {
  const rings = [];
  for (const { name, rings: parts } of polygonPartsFromFeatures(features, nameField)) {
    for (const path of parts) rings.push({ name, path });
  }
  return rings;
}

/**
 * Polygon parts with rings grouped, for drawing fills.
 *
 * Kept separate from ringsFromFeatures because fills and outlines need opposite
 * groupings: an outline wants every ring flat, whereas a fill must keep a hole
 * attached to its outer ring or the hole gets painted in as solid land.
 *
 * Google Maps treats trailing entries in `paths` as holes when their winding
 * runs opposite the outer ring, which the GeoJSON spec already requires
 * (outer counter-clockwise, holes clockwise), so no rewinding is done here.
 *
 * @returns {Array<{name: string|null, paths: Array<Array<{lat: number, lng: number}>>}>}
 */
export function polygonsFromFeatures(features, nameField = 'NAME') {
  return polygonPartsFromFeatures(features, nameField).map(({ name, rings }) => ({
    name,
    paths: rings,
  }));
}

// Dash geometry, expressed relative to the stroke weight rather than as fixed
// pixel values. A plain SVG path string is used rather than
// google.maps.SymbolPath so these stay pure values and can be asserted in tests
// without the Maps SDK loaded.
//
// WHY RELATIVE: the dash icon is a 2-unit line scaled by `scale`, and its
// THICKNESS is the stroke weight. Those were independent constants, so thickening
// a boundary made a dotted line's dots 2.8px long by 3px thick — square blobs,
// and past that a solid line. The stroke pattern is what carries the
// "don't distinguish by hue alone" requirement, so degrading it silently breaks
// accessibility, not just looks. Deriving the geometry from the weight keeps each
// dash's proportions and its on/off rhythm fixed at any thickness.
//
// Both ratios are multiples of the stroke weight, so the ink/gap rhythm is fixed
// by construction rather than by arithmetic that has to be kept in step:
//   lengthRatio: dash length / weight
//   repeatRatio: dash-to-dash pitch / weight
// At weight 2 these reproduce the previous literals exactly — dashed
// scale 2.4 / repeat 14px, dotted scale 1.4 / repeat 7px.
const STROKE_PATTERNS = {
  solid: null,
  dashed: { lengthRatio: 2.4, repeatRatio: 7 },
  dotted: { lengthRatio: 1.4, repeatRatio: 3.5 },
};

/**
 * Dash geometry in Google Maps `icons` terms for a given stroke weight.
 *
 * @param {{lengthRatio: number, repeatRatio: number}} pattern
 * @param {number} weight stroke weight in px
 * @returns {{scale: number, repeat: string}}
 */
export function dashGeometryFor(pattern, weight) {
  return {
    // The icon path spans 2 units, so scale is half the length we want.
    scale: (pattern.lengthRatio * weight) / 2,
    repeat: `${pattern.repeatRatio * weight}px`,
  };
}

// Fill strength lives in lib/fillOpacity.js, shared with the legend swatch so the
// key cannot drift from what the map draws.

/** True when the boundary is drawn by a separate patterned Polyline. */
function hasPatternedOutline(source) {
  return Boolean(STROKE_PATTERNS[source.outlineStyle ?? 'solid']);
}

/**
 * Google Maps PolygonOptions for a source's fill.
 *
 * The stroke is suppressed here when the outline is patterned, because a
 * Polygon cannot draw a dashed edge — a companion Polyline does that. For solid
 * layers the Polygon draws its own edge and no Polyline is created at all.
 *
 * @param {object} source catalogue source
 * @returns {object} PolygonOptions (minus `paths`, added per polygon part)
 */
export function polygonOptionsFor(source) {
  const colour = source.outlineColour ?? '#000000';
  const patterned = hasPatternedOutline(source);

  return {
    fillColor: source.fillColour ?? colour,
    fillOpacity: source.fillOpacity ?? DEFAULT_FILL_OPACITY,
    strokeColor: colour,
    strokeWeight: source.outlineWeight ?? 2,
    strokeOpacity: patterned ? 0 : 1,
    // Non-interactive: these sit above the farmer's own zone polygons, so a
    // clickable fill would eat the tap that selects a zone. Turn back on with
    // the identify handler (bbbFeatureName is already set on every shape).
    clickable: false,
    zIndex: source.precedence ?? 100,
  };
}

/**
 * Google Maps PolylineOptions for a source's outline style.
 *
 * Patterned lines set strokeOpacity 0 on the base line so only the repeated
 * icons show — that is how Google Maps expresses a dashed stroke.
 *
 * @param {object} source catalogue source (outlineColour, outlineStyle, ...)
 * @returns {object} PolylineOptions (minus `path`, added per ring)
 */
export function polylineOptionsFor(source) {
  const colour = source.outlineColour ?? '#000000';
  const weight = source.outlineWeight ?? 2;
  const pattern = STROKE_PATTERNS[source.outlineStyle ?? 'solid'];

  const options = {
    strokeColor: colour,
    strokeWeight: weight,
    strokeOpacity: pattern ? 0 : 1,
    // See polygonOptionsFor: non-interactive so zone selection still works.
    clickable: false,
    zIndex: source.precedence ?? 100,
  };

  if (pattern) {
    const { scale, repeat } = dashGeometryFor(pattern, weight);
    options.icons = [
      {
        icon: {
          path: 'M 0,-1 0,1',
          strokeColor: colour,
          strokeOpacity: 1,
          strokeWeight: weight,
          scale,
        },
        offset: '0',
        repeat,
      },
    ];
  }

  return options;
}

/**
 * Create a vector outline layer for one catalogue source.
 *
 * Returns an imperative handle rather than an overlayMapType, because these are
 * Polylines attached directly to the map — they are reconciled by
 * useVectorLayerController, not by map.overlayMapTypes.
 *
 * @param {object} source catalogue source with kind 'arcgis-vector'
 * @param {object} [deps] injectable for tests
 * @param {Function} [deps.onStats] called once per refresh with what it cost.
 *   A callback rather than an import so this adapter stays unaware of the
 *   metrics harness, and so the superseded and failed paths — which return
 *   undefined and would otherwise be invisible — can still report.
 * @returns {{refresh: Function, clear: Function, destroy: Function}}
 */
export function createVectorOutlineLayer(source, deps = {}) {
  const fetchFeatures = deps.fetchViewportFeatures ?? fetchViewportFeatures;
  const PolylineCtor = deps.Polyline ?? window.google?.maps?.Polyline;
  const PolygonCtor = deps.Polygon ?? window.google?.maps?.Polygon;
  const onStats = deps.onStats;

  // Every shape this layer owns — fill Polygons and, for patterned layers, the
  // companion outline Polylines. Held together so clear() cannot leak one kind.
  let shapes = [];
  let map = null;
  // Monotonic token so a slow in-flight response for an old viewport cannot
  // overwrite the current one (pan/zoom fires far faster than these resolve).
  let requestToken = 0;

  function clear() {
    // Invalidate any in-flight response as well as removing what is drawn.
    // Without this, toggling the layer off mid-fetch lets the response land
    // afterwards and repaint — and useVectorLayerController has already dropped
    // the handle by then, so nothing is left that can clear those shapes again.
    requestToken += 1;
    for (const shape of shapes) shape.setMap(null);
    shapes = [];
  }

  /**
   * Fetch the viewport and repaint this layer.
   *
   * @returns {Promise<{truncated: boolean}|undefined>} what was rendered, or
   *   undefined when nothing was repainted (a failed fetch, or a response
   *   superseded by a newer viewport) so the caller leaves the current status
   *   alone. `truncated` true means the fetch hit its page cap, so the render is
   *   missing features — discarding it was BBB-522. Cost is reported separately
   *   through deps.onStats, including on the paths that return undefined.
   */
  async function refresh(targetMap, bounds, zoom) {
    map = targetMap;
    const token = ++requestToken;

    let features;
    let truncated;
    let fetchMs;
    let bytes;
    let pages;
    try {
      ({ features, truncated, ms: fetchMs, bytes, pages } = await fetchFeatures(
        source,
        bounds,
        zoom,
      ));
    } catch (err) {
      // A failed designation fetch must not blank the map or break sibling
      // layers, so the previous render stays in place. The 'failed' stat below
      // is what carries the failure onward; since BBB-527 the health tracker
      // turns it into "Not available right now" for the farmer, so this line is
      // a developer diagnostic rather than the only trace it ever happened.
      reportError(`[${source.id}] vector fetch failed:`, err);
      onStats?.({ outcome: 'failed' });
      return;
    }

    if (token !== requestToken) {
      // Superseded, but the bytes were still downloaded and still competed with
      // the tiles the farmer is waiting for. Reporting it as wasted work is the
      // whole point — silence here would make a fast pan look free.
      onStats?.({ outcome: 'superseded', fetchMs, bytes, pages, features: features.length });
      return;
    }

    const renderStartedAt = nowMs();
    clear();

    // Fill first: one Polygon per polygon part, rings grouped so holes stay holes.
    const polygonOptions = polygonOptionsFor(source);
    for (const { name, paths } of polygonsFromFeatures(features, source.nameField)) {
      const polygon = new PolygonCtor({ ...polygonOptions, paths });
      polygon.setMap(map);
      if (name) polygon.set('bbbFeatureName', name);
      shapes.push(polygon);
    }

    // Patterned boundaries only: a solid layer's edge is already drawn by its
    // Polygon, so adding Polylines there would double every stroke.
    if (hasPatternedOutline(source)) {
      const lineOptions = polylineOptionsFor(source);
      for (const { name, path } of ringsFromFeatures(features, source.nameField)) {
        const line = new PolylineCtor({ ...lineOptions, path });
        line.setMap(map);
        if (name) line.set('bbbFeatureName', name);
        shapes.push(line);
      }
    }

    onStats?.({
      outcome: 'rendered',
      fetchMs,
      bytes,
      pages,
      features: features.length,
      shapes: shapes.length,
      renderMs: nowMs() - renderStartedAt,
      truncated: Boolean(truncated),
    });

    // Deliberately unchanged: the return value is the status contract that
    // statusForRefresh reads, and metrics have their own channel in onStats.
    // Widening this to carry stats would couple the two.
    return { truncated: Boolean(truncated) };
  }

  return {
    refresh,
    clear,
    destroy: clear,
    // Exposed for assertions and for the identify handler.
    getShapes: () => shapes,
  };
}
